/* ══════════════════════════════════════════════════════════════════════════
 * THE PASSWORD RULES, FOR REACT
 *
 * ⚠️ THIS IS A SECOND COPY OF `Eleater Prototype/eleat_password.js` AND I DO
 * NOT GET TO PRETEND OTHERWISE. That file is an IIFE that writes innerHTML and
 * hangs itself off `window`; React cannot import it and must not load it. So
 * the two paths exist, exactly as the eye icon in SignIn.jsx does.
 *
 * ⚠️ EVERY OTHER DUPLICATE IN THIS REPOSITORY DRIFTED. The /v1 prefix, sixteen
 * connector files, two sign-in doors, two lists of folders, two cuisine lists
 * that ended up sharing sixteen terms. Not one of them was noticed by the
 * person who made the second copy — they were noticed weeks later by Al.
 *
 * So this copy is CHECKED rather than trusted:
 * `tools/generators/verify_password_rules.js` runs the vanilla functions and
 * these functions over the same inputs and fails if a single answer differs.
 * A duplicate with a gate on it is a duplicate that cannot drift quietly. A
 * duplicate without one is the fault this project has spent a fortnight
 * clearing out.
 *
 * ⚠️ THE MINIMUM IS EIGHT AND IT IS NOT TYPED HERE AS A LOOSE NUMBER. The
 * vanilla file's own words: "A minimum typed onto four screens is four
 * minimums the day one of them changes." The gate is what keeps these two in
 * step; migration 085 and the route's `z.string().min(8)` are the third and
 * fourth, and the gate checks the route too.
 *
 * ⚠️⚠️ AND THERE ARE COMPOSITION RULES NOW — AL-NOOR, 9 OCTOBER 2026. A
 * tester was told only after typing that a password needed eight characters:
 * "we should make the characters with numbers and letters, capitals, and…
 * the at sign or other signs like that. And we should actually have it
 * written there." RULE is shown before anything is typed; a password needs
 * 8 characters · a capital · a small letter · a number · a symbol. The server
 * (src/lib/password_rule.js) refuses the same.
 * ══════════════════════════════════════════════════════════════════════════ */

export const MIN = 8;

/** Written on the screen before anything is typed. */
export const RULE = 'At least 8 characters, with a capital letter, a small letter, a number and a symbol (like @ ! $ &).';

/* ⚠️ THE WHOLE RULE STAYS ON SCREEN AS A CHECKLIST — Al-Noor, 10 Oct 2026.
   Testers in Calgary saw only "Add a symbol, like @ ! $ &" under a greyed
   button and did not know what was wrong. Best practice (Mailchimp's sign-up,
   web.dev, uxcel): every requirement listed under the box, each ticked as it
   is met. The words are RULE's own, one line each. */
export const CHECKS = [
  { label: `At least ${MIN} characters`,   ok: (pw) => pw.length >= MIN },
  { label: 'A capital letter (A–Z)',        ok: (pw) => /[A-Z]/.test(pw) },
  { label: 'A small letter (a–z)',          ok: (pw) => /[a-z]/.test(pw) },
  { label: 'A number (0–9)',                ok: (pw) => /[0-9]/.test(pw) },
  { label: 'A symbol, like @ ! $ &',        ok: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

/** The first thing still missing, in plain words — or null. */
export function needs(pw) {
  pw = String(pw || '');
  if (pw.length < MIN) return `Your password needs at least ${MIN} characters.`;
  if (!/[A-Z]/.test(pw)) return 'Add a capital letter.';
  if (!/[a-z]/.test(pw)) return 'Add a small letter.';
  if (!/[0-9]/.test(pw)) return 'Add a number.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Add a symbol, like @ ! $ &';
  return null;
}

/** How strong is it — what is still missing, then Good / Strong. */
export function strength(pw) {
  pw = String(pw || '');
  if (!pw) return { level: 0, label: '' };
  const n = needs(pw);
  if (n) return { level: 0, label: n };
  if (pw.length >= 12) return { level: 3, label: 'Strong' };
  return { level: 2, label: 'Good' };
}

/**
 * Is it usable yet — the sentence to show, or null when nothing is wrong.
 * One function, so the button, the inline line and the gate cannot disagree.
 * The server checks all of it again; a browser check is a courtesy.
 */
export function problem(pw, confirm) {
  pw = String(pw || '');
  confirm = String(confirm == null ? '' : confirm);
  if (!pw) return 'Choose a password.';
  const n = needs(pw);
  if (n) return n;
  /* ⚠️ THERE IS NO SKIP. Al, 16 September 2026: "you have to sign up with a
     confirmation. Like, it goes hand in hand." */
  if (!confirm) return 'Type your password again to confirm it.';
  if (pw !== confirm) return 'Those two passwords are not the same.';
  return null;
}
